
import React from 'react';
import { toPascalCase } from "@/utils/text";
import { EnrichedVisitor } from "@/types/database";
import { ExternalLink } from "lucide-react";

interface CompanyDetailsProps {
  visitor: EnrichedVisitor;
  companyInitials: string;
  formatPageUrl: (url: string | null) => string;
}

const CompanyDetails = ({ visitor, companyInitials, formatPageUrl }: CompanyDetailsProps) => {
  // Format website for display and linking
  const websiteUrl = visitor.company_website ? 
    (visitor.company_website.startsWith('http') ? visitor.company_website : `https://${visitor.company_website}`) 
    : null;

  // Function to format confidence into a styled badge
  const getConfidenceBadge = (confidence: string | null | undefined) => {
    if (!confidence) return null;
    
    const confidenceLower = confidence.toLowerCase();
    let bgColor = "bg-gray-100";
    let textColor = "text-gray-600";
    
    if (confidenceLower === "high") {
      bgColor = "bg-green-100";
      textColor = "text-green-700";
    } else if (confidenceLower === "medium") {
      bgColor = "bg-yellow-100";
      textColor = "text-yellow-700";
    } else if (confidenceLower === "low") {
      bgColor = "bg-red-100";
      textColor = "text-red-700";
    }
    
    return (
      <span className={`text-xs font-medium ${bgColor} ${textColor} px-2 py-1 rounded-full ml-2`}>
        {toPascalCase(confidence)}
      </span>
    );
  };

  return (
    <>
      <div className="flex items-center space-x-4">
        <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center text-xl font-semibold text-gray-600">
          {companyInitials}
        </div>
        <div>
          <div className="flex items-center">
            <h3 className="text-lg font-semibold text-gray-900">{toPascalCase(visitor.company_name)}</h3>
            {visitor.company_confidence && getConfidenceBadge(visitor.company_confidence)}
          </div>
          {websiteUrl ? (
            <a 
              href={websiteUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
            >
              {visitor.company_website}
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <p className="text-sm text-gray-500">Website not available</p>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium text-gray-500">Company Size</p>
          <p className="mt-1 text-base text-gray-900">{visitor.company_size || 'Not available'}</p>
        </div>

        <div>
          <p className="text-sm font-medium text-gray-500">Industry</p>
          <p className="mt-1 text-base text-gray-900">{toPascalCase(visitor.company_industry) || 'Not available'}</p>
        </div>

        <div>
          <p className="text-sm font-medium text-gray-500">Location</p>
          <p className="mt-1 text-base text-gray-900">
            {visitor.location_locality && visitor.location_country
              ? `${toPascalCase(visitor.location_locality)}, ${toPascalCase(visitor.location_country)}`
              : 'Not available'}
          </p>
        </div>

        <div>
          <p className="text-sm font-medium text-gray-500">Visited Page</p>
          <p className="mt-1 text-base text-gray-900 break-words">
            {formatPageUrl(visitor.page_url)}
          </p>
        </div>

        <div>
          <p className="text-sm font-medium text-gray-500">Visit Date</p>
          <p className="mt-1 text-base text-gray-900">
            {new Date(visitor.created_at).toLocaleString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
              hour12: true
            })}
          </p>
        </div>
      </div>
    </>
  );
};

export default CompanyDetails;
